import React, { useEffect, useState } from 'react';
import { getFramework, RenderStage, useIdentity, useViewHeight, useViewWidth } from '@/framework';
import { computeAutoScale } from '@/utils/stage-kit';
import { ILayerProps, IMainLayerProps } from '@/version-types';
import { useStageCropStore } from '../models/crop-stage-state';
import CropStageWrap from './crop-stage-wrap/crop-stage-wrap';
import MouseScaleAction from './operate-stage/mouse-scale-action';
import OperateStage from './operate-stage/operate-stage';
import ScaleStage from './scale-stage/scale-stage';

const RenderCropStage: React.FC = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const originIndex = framework.stage.getActiveOriginIndex();

    const { isCropStage, currentCropStageScale, isCropStageOperating, setCurrentCropStageScale } = useStageCropStore()(
        (state) => state,
    );

    const [mainLayer, setMainLayer] = useState<ILayerProps>(framework.getMainLayer(originIndex)!);
    const [isResize, setIsResize] = useState(Math.random());

    useEffect(() => {
        const handler = framework.events.onLayerChange(() => {
            setMainLayer(framework.getMainLayer(originIndex)!);
        }, mainLayer.id);

        return () => {
            framework.events.off(handler);
        };
    }, [mainLayer.id]);
    const { width: stageWidth, height: stageHeight } = mainLayer;
    const currentWidth = useViewWidth();
    const currentHeight = useViewHeight();

    const [isSetScale, setIsSetScale] = useState(false);

    useEffect(() => {
        if (isCropStageOperating) {
            return;
        }

        if (!currentWidth || !currentHeight) {
            return;
        }

        const reScaleHandler = () => {
            const scale = computeAutoScale(
                currentWidth - 160,
                currentHeight - 160,
                stageWidth,
                stageHeight,
                framework.stage.getViewPortPadding(),
            );
            setIsResize(Math.random());
            setCurrentCropStageScale(scale);
        };

        reScaleHandler();

        setTimeout(() => {
            setIsSetScale(true);
        });

        window.addEventListener('resize', reScaleHandler);

        return () => {
            window.removeEventListener('resize', reScaleHandler);
        };
    }, [stageWidth, stageHeight, currentWidth, currentHeight, isCropStageOperating, mainLayer.id]);

    if (!isSetScale) {
        return null;
    }

    return (
        <MouseScaleAction stageScale={currentCropStageScale}>
            <CropStageWrap>
                <ScaleStage
                    stageWidth={stageWidth}
                    stageHeight={stageHeight}
                    scale={currentCropStageScale}
                    isResize={isResize}
                >
                    <RenderStage isShowStageMask={!isCropStage} />
                </ScaleStage>
                <OperateStage
                    isResize={isResize}
                    stageWidth={stageWidth}
                    stageHeight={stageHeight}
                    scale={currentCropStageScale}
                    maskURL={(mainLayer?.data as IMainLayerProps).maskURL || ''}
                    framework={framework}
                />
            </CropStageWrap>
        </MouseScaleAction>
    );
});

export default RenderCropStage;
